/**
 * LobbySession state machine — hand-traced 2026-05-14.
 *
 *   T0 init() called
 *     → snapshot = { bootPhase: 'identity', error: null, view: null, connection: 'live' }
 *
 *   T1 identity.getCurrentUser() resolves
 *     → snapshot = { bootPhase: 'connecting', error: null, view: null, connection: 'live' }
 *     T1a identity rejects with err classified as denial (e.g., user clicked Cancel)
 *       → snapshot = { bootPhase: 'failed',
 *                      error: { kind: 'identity_denied', cause: err },
 *                      view: null, connection: 'live' }
 *     T1b identity rejects with anything else (SDK init failure, RPC error, network)
 *       → snapshot = { bootPhase: 'failed',
 *                      error: { kind: 'identity_error', cause: err },
 *                      view: null, connection: 'live' }
 *
 *   T2 new Client(endpoint) succeeds
 *     (Colyseus Client construction is synchronous in 0.16 — no async failure here.)
 *     → snapshot = { bootPhase: 'joining', error: null, view: null, connection: 'live' }
 *
 *   T3 client.reconnect(token) OR client.joinOrCreate('susdle') resolves
 *     → NO snapshot change. colyseus.js resolves the join on JOIN_ROOM,
 *       BEFORE the initial ROOM_STATE arrives — room.state is still a
 *       default-constructed GameState (phase: Lobby, empty players) for one
 *       RTT. Publishing it would emit a phantom Lobby snapshot that corrupts
 *       cold-arrival classification downstream (usePhaseRouter latches
 *       prevPhase from it; AudioDirector would diff against it).
 *
 *   T3½ first ROOM_STATE arrives (room.onStateChange)
 *     → snapshot = { bootPhase: 'ready', error: null,
 *                    view: <first snapshot from state mapping>,
 *                    connection: 'live' }   (one atomic publication)
 *     T3a join rejects with err classified as schema_mismatch (Epic 15 will trigger)
 *       → snapshot = { bootPhase: 'failed',
 *                      error: { kind: 'schema_mismatch', cause: err },
 *                      view: null, connection: 'schemaMismatch' }
 *     T3b join rejects with anything else (matchmaker, connect, capacity, game-in-progress)
 *       → snapshot = { bootPhase: 'failed',
 *                      error: { kind: 'join_error' | 'room_full' | 'game_in_progress', cause: err },
 *                      view: null, connection: 'live' }
 *
 *   T4 mid-game: room.onStateChange fires
 *     → snapshot.view updated; bootPhase stays 'ready'; connection stays 'live'
 *
 *   T5 mid-game: room.onLeave fires (socket dropped, intentionalLeave === false)
 *     → snapshot = { bootPhase: 'reconnecting', error: null,
 *                    view: <last known>, connection: 'connectionLost' }
 *     → exponential-backoff retry loop kicks off (1s → 2s → 4s → 8s → 16s → 30s,
 *       ~60s total budget). Sentry breadcrumb emitted.
 *       Consented close (4000 — kick / rate-limit / redeploy) skips the loop:
 *       the server hard-deleted the seat, so the token is cleared and the
 *       first attempt goes straight to joinOrCreate.
 *     T5a a retry attempt succeeds within budget
 *       → room attached; 'ready' arrives at T3½ (first ROOM_STATE), as above
 *     T5b reconnect(token) rejects deterministically (token expired / room
 *         disposed — the Lobby/GameEnd hard-delete path)
 *       → token cleared; next attempt (immediate) falls back to
 *         joinOrCreate with the boot joinOptions
 *     T5c the joinOrCreate fallback rejects with a classified matchmaker
 *         error (room_full / game_in_progress / schema_mismatch)
 *       → snapshot = { bootPhase: 'failed', error: { kind, cause } } now —
 *         retrying cannot change a deterministic rejection
 *     T5d budget exhausts (~60s elapsed, last attempt failed transiently)
 *       → snapshot = { bootPhase: 'failed',
 *                      error: { kind: 'disconnected', cause: err },
 *                      view: <last known>, connection: 'connectionLost' }
 *       → App routes through bootErrorCopy → ClosedDoorCard 'couldntConnect'.
 *         "Try again" reloads the iframe in place; boot's tryReconnectThenFallback
 *         reclaims the seat from the still-valid server grace window.
 *
 *   T6 graceful leave()
 *     → reconnect token cleared; current room left (the await is skipped when
 *       the socket already dropped — a destroyed room's leave() never
 *       settles); bootPhase moves through 'failed' (with no specific error)
 *       or simply session is disposed.
 *
 * Implementation: handleLeave drives the retry loop directly (no public
 * attemptReconnect method).
 */

import type { IdentityProvider } from '../identity/IdentityProvider.js';
import type { ColyseusClientPort, ColyseusRoomPort } from './colyseusClientPort.js';
import type { ReconnectTokenStore } from './ReconnectTokenStore.js';
import type { LobbyView } from '../lobby/types.js';
import type { MeetingView } from '../ghost-meeting/types.js';
import type { CluegivingView } from '../clue-giving/types.js';
import type { DiscussionView, RoleRevealView, GhostGuessingView, GuessRevealView } from '../discussion/types.js';
import type { GameEndView } from '../game-end/types.js';
import { stateToCluegivingView } from './stateToCluegivingView.js';
import { stateToDiscussionView } from './stateToDiscussionView.js';
import { stateToRoleRevealView, stateToGhostGuessingView, stateToGuessRevealView } from './stateToRevealViews.js';
import { stateToGameEndView } from './stateToGameEndView.js';
import * as Sentry from '@sentry/browser';
import { GamePhase, SCHEMA_VERSION, type TimerPreset } from '@susdle/shared';
import { stateToView } from './stateToView.js';
import { stateToMeetingView } from './stateToMeetingView.js';
import { isConsentedClose, isDeadTokenError } from './classifyDisconnect.js';

interface JoinOptions {
  id: string;
  name: string;
  avatarUrl: string;
  schemaVersion: number;
  instanceId: string;
}

export type BootPhase =
  | 'identity' | 'connecting' | 'joining'
  | 'ready' | 'reconnecting' | 'failed';

export type ErrorKind =
  | 'identity_denied' | 'identity_error'
  | 'connect_error' | 'join_error' | 'disconnected' | 'schema_mismatch'
  | 'room_full' | 'game_in_progress';

export interface Snapshot {
  bootPhase: BootPhase;
  error: { kind: ErrorKind; cause?: unknown } | null;
  /** Lobby view — non-null iff phase is Lobby (and unhandled non-meeting phases). */
  view: LobbyView | null;
  connection: 'live' | 'connectionLost' | 'schemaMismatch';
  /** Server-clock offset in ms (serverNow ≈ Date.now() + clockOffset). 0 until the first pong. */
  clockOffset: number;
  /** Current game phase (drives App routing). */
  phase: GamePhase;
  /** Meeting view — non-null iff `phase === GhostMeeting`. */
  meetingView: MeetingView | null;
  /** Clue-giving view — non-null iff `phase === ClueGiving`. */
  cluegivingView: CluegivingView | null;
  /** Discussion view — non-null iff `phase === Discussion`. */
  discussionView: DiscussionView | null;
  /** RoleReveal view — non-null iff `phase === RoleReveal`. */
  roleRevealView: RoleRevealView | null;
  /** GhostGuessing view — non-null iff `phase === GhostGuessing`. */
  ghostGuessingView: GhostGuessingView | null;
  /** GuessReveal view — non-null iff `phase === GuessReveal`. */
  guessRevealView: GuessRevealView | null;
  /** GameEnd view — non-null iff `phase === GameEnd`. */
  gameEndView: GameEndView | null;
}

export interface LobbySessionDeps {
  identity: IdentityProvider;
  colyseus: ColyseusClientPort;
  reconnectStorage: ReconnectTokenStore;
  /**
   * Discord Activity instance id, threaded into `joinOrCreate` options as the
   * Colyseus matchmaking key. Kept on `LobbySessionDeps` rather than folded
   * into `IdentityProvider`: it's session context (available at SDK
   * construction, before OAuth), not a property of *who* the user is.
   * Unit tests pass a literal (e.g. `'test-instance'`); App resolves it from
   * the Discord SDK adapter; dev-real resolves it from the `?instance_id=`
   * URL param.
   */
  instanceId: string;
  /**
   * Classifies a Discord identity rejection into 'identity_denied' (user
   * declined OAuth consent) vs the generic 'identity_error'. Defaults to a
   * stub returning 'identity_error' when omitted (e.g., unit tests).
   */
  classifyIdentityError?: (err: unknown) => 'identity_denied' | 'identity_error';
  /** When join fails, classify into a specific ErrorKind. Defaults to 'join_error'. */
  classifyJoinError?: (err: unknown) => ErrorKind;
  logger?: { info: (...a: unknown[]) => void; warn: (...a: unknown[]) => void; error: (...a: unknown[]) => void };
}

export class LobbySession {
  private snapshot: Snapshot = { bootPhase: 'identity', error: null, view: null, connection: 'live', clockOffset: 0, phase: GamePhase.Lobby, meetingView: null, cluegivingView: null, discussionView: null, roleRevealView: null, ghostGuessingView: null, guessRevealView: null, gameEndView: null };
  private listeners = new Set<() => void>();
  private inFlightInit: Promise<void> | null = null;
  private currentRoom: ColyseusRoomPort | null = null;
  private meId: string | null = null;
  private intentionalLeave = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryStartedAt = 0;
  /** Boot joinOptions, kept for the in-session loop's joinOrCreate fallback. */
  private joinOptions: JoinOptions | null = null;
  /** True from onLeave until the next room attaches — the socket is gone. */
  private disconnected = false;

  constructor(private readonly deps: LobbySessionDeps) {}

  getSnapshot(): Snapshot { return this.snapshot; }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setSnapshot(next: Partial<Snapshot>): void {
    this.snapshot = { ...this.snapshot, ...next };
    for (const l of this.listeners) l();
  }

  sendFoolToggle(enabled: boolean): void {
    this.currentRoom?.send('updateSettings', { foolEnabled: enabled });
  }

  sendTimerChange(preset: TimerPreset): void {
    this.currentRoom?.send('updateSettings', { timerPreset: preset });
  }

  sendStart(): void {
    this.currentRoom?.send('startGame');
  }

  sendGhostMeetingVote(targetId: string): void {
    this.currentRoom?.send('ghostMeetingVote', { targetId });
  }

  sendSubmitClue(text: string): void {
    this.currentRoom?.send('submitClue', { text });
  }

  sendChangeVote(targetId: string): void {
    this.currentRoom?.send('changeVote', { targetId });
  }

  sendSubmitGuess(word: string): void {
    this.currentRoom?.send('submitGuess', { word });
  }

  sendBackToLobby(): void {
    this.currentRoom?.send('backToLobby');
  }

  async leave(): Promise<void> {
    this.intentionalLeave = true;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.deps.reconnectStorage.clear();
    const room = this.currentRoom;
    this.currentRoom = null;
    /* Skip the await once the socket dropped: colyseus.js Room.destroy never
       nulls room.connection, and LEAVE_ROOM on a CLOSED WebSocket is silently
       discarded — leave() on a destroyed room returns a promise that never
       settles, which would hang this method forever. */
    if (room !== null && !this.disconnected) await room.leave();
  }

  dispose(): void {
    // Set intentionalLeave so any in-flight tryReconnect that resolves after
    // disposal bails via the post-await guard instead of re-attaching to a
    // session that no longer has subscribers.
    this.intentionalLeave = true;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.listeners.clear();
    this.currentRoom = null;
  }

  /**
   * Idempotent boot. Returns the same in-flight promise on repeat calls
   * (React 18 StrictMode safe).
   *
   * **Fire-and-observe, not fire-and-await.** The returned promise always
   * resolves to `void`, even on failure — errors during the state machine
   * are captured into `snapshot.error` and `snapshot.bootPhase === 'failed'`.
   * Callers must `subscribe(...)` or read `getSnapshot()` to observe outcome.
   * This matches the `useSyncExternalStore` contract: the store is the
   * single source of truth; the boot promise just signals completion of
   * the async path.
   */
  init(): Promise<void> {
    if (this.inFlightInit !== null) return this.inFlightInit;
    this.inFlightInit = this.runInit().catch((err) => {
      this.deps.logger?.error('[lobby-session] init failed', err);
    });
    return this.inFlightInit;
  }

  private async runInit(): Promise<void> {
    // identity
    let user;
    try {
      user = await this.deps.identity.getCurrentUser();
    } catch (err) {
      const classifier = this.deps.classifyIdentityError ?? (() => 'identity_error' as const);
      this.setSnapshot({ bootPhase: 'failed', error: { kind: classifier(err), cause: err } });
      return;
    }
    this.meId = user.id;
    this.setSnapshot({ bootPhase: 'connecting' });

    // connecting — Colyseus Client construction is synchronous on 0.16; no async failure here.
    this.setSnapshot({ bootPhase: 'joining' });

    // joining — pass identity so SusdleRoom.onSusdleJoin can stamp the Player.
    // Without `id`, the server falls back to client.sessionId in onSusdleJoin,
    // which makes every reconnect (and every StrictMode re-mount) look like a
    // brand-new player. `instanceId` is the Colyseus matchmaking key: all
    // clients in the same Discord Activity instance carry the same value and
    // resolve to the same room via `filterBy(['instanceId'])` on the server.
    const joinOptions: JoinOptions = {
      id: user.id,
      name: user.name,
      avatarUrl: user.avatarUrl,
      schemaVersion: SCHEMA_VERSION,
      instanceId: this.deps.instanceId,
    };
    this.joinOptions = joinOptions;
    try {
      const storedToken = this.deps.reconnectStorage.read();
      const room = storedToken !== null
        ? await this.tryReconnectThenFallback(storedToken, joinOptions)
        : await this.deps.colyseus.joinOrCreate('susdle', joinOptions);
      if (this.intentionalLeave) {
        // leave()/dispose() raced the join await — release the seat instead
        // of attaching a room nobody observes (zombie seat server-side).
        // Mirrors tryReconnect's post-await guard.
        void room.leave();
        return;
      }
      this.attachRoom(room);
    } catch (err) {
      const classifier = this.deps.classifyJoinError ?? (() => 'join_error' as const);
      const kind = classifier(err);
      this.setSnapshot({
        bootPhase: 'failed',
        error: { kind, cause: err },
        connection: kind === 'schema_mismatch' ? 'schemaMismatch' : 'live',
      });
      return;
    }
    // 'ready' is NOT set here — it arrives with the first ROOM_STATE (T3½),
    // so the first ready snapshot always carries real server state.
  }

  private async tryReconnectThenFallback(
    token: string,
    joinOptions: JoinOptions,
  ): Promise<ColyseusRoomPort> {
    try {
      return await this.deps.colyseus.reconnect(token);
    } catch (err) {
      this.deps.logger?.warn('[lobby-session] reconnect failed; falling back to fresh join', err);
      this.deps.reconnectStorage.clear();
      return await this.deps.colyseus.joinOrCreate('susdle', joinOptions);
    }
  }

  private attachRoom(room: ColyseusRoomPort): void {
    this.currentRoom = room;
    this.disconnected = false;
    this.deps.reconnectStorage.save(room.reconnectionToken);
    /* The first publication — and the 'ready' / 'live' flip — waits for the
       first ROOM_STATE (see T3/T3½ in the header): until it arrives,
       room.state is a default-constructed GameState and publishing it would
       emit a phantom Lobby snapshot. Folding the boot fields into the same
       refreshView publication keeps the ready transition atomic: subscribers
       never observe 'ready' paired with a stale phase. */
    let hydrated = false;
    room.onStateChange(() => {
      if (hydrated) {
        this.refreshView();
        return;
      }
      hydrated = true;
      this.refreshView({ bootPhase: 'ready', connection: 'live', error: null });
    });
    room.onLeave((code, reason) => this.handleLeave(code, reason));
    this.startTimeSync(room);
  }

  private refreshView(boot?: Partial<Pick<Snapshot, 'bootPhase' | 'connection' | 'error'>>): void {
    if (this.currentRoom === null || this.meId === null) return;
    const state = this.currentRoom.state;
    const meId = this.meId;
    const cleared = {
      view: null, meetingView: null, cluegivingView: null,
      discussionView: null, roleRevealView: null,
      ghostGuessingView: null, guessRevealView: null,
      gameEndView: null,
      ...boot,
    };
    switch (state.phase) {
      case GamePhase.GhostMeeting:
        this.setSnapshot({ ...cleared, phase: state.phase, meetingView: stateToMeetingView(state, { meId }) });
        return;
      case GamePhase.ClueGiving:
        this.setSnapshot({ ...cleared, phase: state.phase, cluegivingView: stateToCluegivingView(state, { meId }) });
        return;
      case GamePhase.Discussion:
        this.setSnapshot({ ...cleared, phase: state.phase, discussionView: stateToDiscussionView(state, { meId }) });
        return;
      case GamePhase.RoleReveal:
        this.setSnapshot({ ...cleared, phase: state.phase, roleRevealView: stateToRoleRevealView(state, { meId }) });
        return;
      case GamePhase.GhostGuessing:
        this.setSnapshot({ ...cleared, phase: state.phase, ghostGuessingView: stateToGhostGuessingView(state, { meId }) });
        return;
      case GamePhase.GuessReveal:
        this.setSnapshot({ ...cleared, phase: state.phase, guessRevealView: stateToGuessRevealView(state, { meId }) });
        return;
      case GamePhase.GameEnd:
        this.setSnapshot({ ...cleared, phase: state.phase, gameEndView: stateToGameEndView(state, { meId }) });
        return;
      default:
        /* Lobby (and unknown phases) map through the lobby mapper;
           the App only renders <Lobby> for phase === Lobby. */
        this.setSnapshot({ ...cleared, phase: state.phase, view: stateToView(state, { meId }) });
    }
  }

  private startTimeSync(room: ColyseusRoomPort): void {
    /* One-shot skew estimate. offset = serverTs - clientTs - rtt/2 (one-way ≈ rtt/2).
       The server's ping handler has no phase guard / rate limit, so a single ping
       right after attach is safe. */
    room.onMessage<{ clientTimestamp: number; serverTimestamp: number }>('pong', (msg) => {
      const rtt = Date.now() - msg.clientTimestamp;
      const offset = msg.serverTimestamp - msg.clientTimestamp - rtt / 2;
      this.setSnapshot({ clockOffset: offset });
    });
    room.send('ping', { clientTimestamp: Date.now() });
  }

  private handleLeave(code: number, reason?: string): void {
    this.disconnected = true;
    if (this.intentionalLeave) {
      this.intentionalLeave = false;
      return;
    }
    Sentry.addBreadcrumb({
      category: 'connection',
      level: 'warning',
      message: 'ws disconnect',
      data: { code, reason },
    });
    this.setSnapshot({ bootPhase: 'reconnecting', connection: 'connectionLost' });
    this.retryStartedAt = Date.now();
    if (isConsentedClose(code)) {
      /* The server closed us on purpose (kick / rate-limit / redeploy) and
         hard-deleted the seat — reconnect(token) would fail instantly and
         deterministically for the whole 60s budget. Clear the dead token so
         the first (immediate) attempt goes straight to joinOrCreate. */
      this.deps.reconnectStorage.clear();
      this.scheduleRetry(0);
      return;
    }
    this.scheduleRetry(1000);
  }

  private scheduleRetry(delayMs: number): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.tryReconnect(delayMs);
    }, delayMs);
  }

  private async tryReconnect(prevDelay: number): Promise<void> {
    const token = this.deps.reconnectStorage.read();
    try {
      /* No token means the seat is already gone (consented close, or a prior
         dead-token rejection cleared it) — re-enter through the front door
         with the boot joinOptions instead of dead-ending. */
      const room = token !== null
        ? await this.deps.colyseus.reconnect(token)
        : await this.joinFresh();
      if (this.intentionalLeave || this.snapshot.bootPhase !== 'reconnecting') {
        // leave() was called or another attempt already won. Release the
        // room we just acquired and bail — do not re-attach.
        void room.leave();
        return;
      }
      this.attachRoom(room);
      // 'ready' arrives with the room's first ROOM_STATE (see attachRoom).
    } catch (err) {
      if (this.intentionalLeave || this.snapshot.bootPhase !== 'reconnecting') return;
      if (token !== null && isDeadTokenError(err)) {
        /* The matchmaker rejected the token deterministically (seat
           hard-deleted in Lobby/GameEnd, TTL expiry, room disposed). Backoff
           cannot change that — clear it and retry NOW via joinOrCreate. */
        this.deps.reconnectStorage.clear();
        this.scheduleRetry(0);
        return;
      }
      if (token === null) {
        const classifier = this.deps.classifyJoinError ?? (() => 'join_error' as const);
        const kind = classifier(err);
        if (kind !== 'join_error') {
          /* Deterministic matchmaker rejection of the fresh join (room_full /
             game_in_progress / schema_mismatch) — terminal, no fake spinner.
             Failure shape mirrors runInit's join-failure branch. */
          this.setSnapshot({
            bootPhase: 'failed',
            error: { kind, cause: err },
            connection: kind === 'schema_mismatch' ? 'schemaMismatch' : 'live',
          });
          return;
        }
      }
      if (Date.now() - this.retryStartedAt >= 60_000) {
        this.setSnapshot({ bootPhase: 'failed', error: { kind: 'disconnected', cause: err } });
      } else {
        // max(…, 1000) keeps the post-fallback cadence sane after a 0ms attempt.
        this.scheduleRetry(Math.min(Math.max(prevDelay * 2, 1000), 30_000));
      }
    }
  }

  private joinFresh(): Promise<ColyseusRoomPort> {
    if (this.joinOptions === null) {
      // Unreachable in practice: handleLeave only fires after attachRoom,
      // which requires runInit to have built joinOptions. Reject into the
      // normal failure path rather than throw synchronously.
      return Promise.reject(new Error('no join options for fallback join'));
    }
    return this.deps.colyseus.joinOrCreate('susdle', this.joinOptions);
  }
}
